/**
 * Sidebar navigation component with role-based filtering
 * SuperAdmin gets a dynamic sidebar:
 *   - Modo Operador (no impersonation): Dashboard Global, Empresas, Herramientas
 *   - Modo Impersonación: same nav as a company admin sees
 */

import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  FileText,
  FolderKanban,
  BarChart3,
  Building2,
  Tags,
  Users,
  UsersRound,
  Palette,
  Building,
  Car,
  ChevronLeft,
  ChevronRight,
  X,
  FileSearch,
  Shield,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CompanySelector } from '../admin/CompanySelector';
import { cn } from '../../lib/utils';
import './Sidebar.css';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  requiredPermission?: keyof ReturnType<typeof usePermissions>;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

/**
 * Navigation sections for SuperAdmin in operator mode (no impersonation).
 */
const superadminSections: NavigationSection[] = [
  {
    title: 'Plataforma',
    items: [
      {
        id: 'admin-dashboard',
        label: 'Dashboard Global',
        icon: LayoutDashboard,
        path: '/admin/dashboard',
      },
      {
        id: 'companies',
        label: 'Empresas',
        icon: Building,
        path: '/admin/companies',
      },
      {
        id: 'admin-reports',
        label: 'Reportes Globales',
        icon: BarChart3,
        path: '/admin/reports',
      },
    ],
  },
  {
    title: 'Herramientas',
    items: [
      {
        id: 'logs',
        label: 'Logs',
        icon: FileSearch,
        path: '/admin/logs',
      },
    ],
  },
];

/**
 * Navigation sections for regular users (and SuperAdmin when impersonating).
 */
const companySections: NavigationSection[] = [
  {
    title: 'Operaciones',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: '/dashboard',
      },
      {
        id: 'clients',
        label: 'Clientes',
        icon: UsersRound,
        path: '/clients',
      },
      {
        id: 'projects',
        label: 'Proyectos',
        icon: FolderKanban,
        path: '/projects',
      },
      {
        id: 'expenses',
        label: 'Gastos',
        icon: Receipt,
        path: '/expenses',
      },
      {
        id: 'invoices',
        label: 'Facturas',
        icon: FileText,
        path: '/invoices',
      },
    ],
  },
  {
    title: 'Análisis',
    items: [
      {
        id: 'reports',
        label: 'Reportes',
        icon: BarChart3,
        path: '/reports',
      },
    ],
  },
  {
    title: 'Configuración',
    items: [
      {
        id: 'my-company',
        label: 'Mi Empresa',
        icon: Building2,
        path: '/my-company',
        requiredPermission: 'canManageCompanySettings',
      },
      {
        id: 'users',
        label: 'Usuarios',
        icon: Users,
        path: '/users',
        requiredPermission: 'canManageUsers',
      },
      {
        id: 'categories',
        label: 'Categorías',
        icon: Tags,
        path: '/categories',
        requiredPermission: 'canManageCategories',
      },
      {
        id: 'assets',
        label: 'Activos',
        icon: Car,
        path: '/assets',
        requiredPermission: 'canManageCompanySettings',
      },
      {
        id: 'branding',
        label: 'Personalización',
        icon: Palette,
        path: '/branding',
        requiredPermission: 'canManageCompanySettings',
      },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen,
  onMobileClose,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { isImpersonating, selectedCompanyId, selectedCompanyName, selectedTradeName, setSelectedCompany } = useAdmin();
  const permissions = usePermissions();
  const location = useLocation();
  const isMobile = window.innerWidth < 768;

  const isSuperAdmin = user?.role === 'superadmin';
  const isOperatorMode = isSuperAdmin && !isImpersonating;

  // Choose sections based on mode
  const baseSections = isOperatorMode ? superadminSections : companySections;

  // Filter items based on permissions
  const filteredSections = baseSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.requiredPermission || permissions[item.requiredPermission]
      ),
    }))
    .filter((section) => section.items.length > 0);

  // Prevent body scroll when mobile overlay is open
  useEffect(() => {
    if (isMobile && isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMobileOpen]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    if (isMobile && isMobileOpen) {
      onMobileClose();
    }
  }, [location.pathname]);

  const sidebarContent = (
    <div className="sidebar-content">
      {/* Logo Section */}
      <div className="sidebar-logo">
        <img
          src={theme.logoUrl || '/default-logo.svg'}
          alt={theme.logoUrl ? 'Company Logo' : 'Kessan'}
          className={`sidebar-logo-image ${(!theme.showCompanyName && !isCollapsed) ? 'sidebar-logo-image-full' : ''}`}
          loading="lazy"
        />
        {!isCollapsed && theme.showCompanyName && (
          <div className="sidebar-logo-content">
            <span className="sidebar-logo-company">
              {isImpersonating
                ? (selectedTradeName || selectedCompanyName || 'Mi Empresa')
                : (user?.tradeName || user?.companyName || 'Mi Empresa')}
            </span>
          </div>
        )}
      </div>

      {/* Company Selector for SuperAdmin — prominent, in sidebar */}
      {isSuperAdmin && !isCollapsed && (
        <div className="px-3 mb-2">
          <CompanySelector
            selectedCompanyId={selectedCompanyId || undefined}
            onCompanySelect={setSelectedCompany}
          />
        </div>
      )}

      {/* Operator mode badge */}
      {isOperatorMode && !isCollapsed && (
        <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <Shield className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          <span className="text-xs font-medium text-indigo-700">Modo Operador</span>
        </div>
      )}
      {isOperatorMode && isCollapsed && (
        <div className="mx-auto mb-3 flex items-center justify-center w-10 h-10 bg-indigo-50 border border-indigo-200 rounded-lg" title="Modo Operador">
          <Shield className="w-4 h-4 text-indigo-600" />
        </div>
      )}

      {/* Navigation Sections */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        {filteredSections.map((section) => (
          <div key={section.title} className="sidebar-section">
            {!isCollapsed && (
              <h3 className="sidebar-section-title">{section.title}</h3>
            )}
            <ul className="sidebar-items">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path));

                return (
                  <li key={item.id}>
                    <Link
                      to={item.path}
                      className={cn('sidebar-item', {
                        'sidebar-item-active': isActive,
                        'sidebar-item-collapsed': isCollapsed,
                      })}
                      aria-label={item.label}
                      aria-current={isActive ? 'page' : undefined}
                      data-testid="nav-item"
                      data-item-id={item.id}
                    >
                      <Icon className="sidebar-item-icon" />
                      {!isCollapsed && (
                        <span className="sidebar-item-label">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="sidebar-toggle"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? (
          <ChevronRight className="sidebar-toggle-icon" />
        ) : (
          <ChevronLeft className="sidebar-toggle-icon" />
        )}
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isMobileOpen && (
          <div
            className="sidebar-overlay"
            onClick={onMobileClose}
            aria-hidden="true"
          />
        )}
        <aside
          className={cn('sidebar sidebar-mobile', {
            'sidebar-mobile-open': isMobileOpen,
          })}
          aria-label="Mobile navigation"
        >
          <button
            onClick={onMobileClose}
            className="sidebar-mobile-close"
            aria-label="Close sidebar"
          >
            <X className="sidebar-mobile-close-icon" />
          </button>
          {sidebarContent}
        </aside>
      </>
    );
  }

  return (
    <aside
      className={cn('sidebar sidebar-desktop', {
        'sidebar-collapsed': isCollapsed,
      })}
      aria-label="Main navigation"
    >
      {sidebarContent}
    </aside>
  );
};

export default Sidebar;
